import {Menu} from "@mantine/core";
import {useTranslation} from "react-i18next";
import {useCallback, useMemo} from "react";
import {useAuth} from "../../../providers/AuthProvider/AuthProvider";
import {GeofenceEventEntity} from "@ots-frontend/ots-data";
import {IconEdit, IconTrash} from "@tabler/icons-react";
import {useGeofenceEventAction} from "./useGeofenceEventAction";

const ALL_GEOFENCE_EVENT_ACTIONS = [
  "EDIT",
  "DELETE",
] as const;

export type GeofenceEventAction = typeof ALL_GEOFENCE_EVENT_ACTIONS[number];

export type GeofenceEventActionMenuDropdownProps = {
  geofenceEvent: GeofenceEventEntity
  actions?: GeofenceEventAction[]
  onActionClick?: (action: GeofenceEventAction) => void
  showEmpty?: boolean
}

export const GeofenceEventActionMenuDropdown = (props: GeofenceEventActionMenuDropdownProps) => {
  const {
    geofenceEvent,
    actions = ALL_GEOFENCE_EVENT_ACTIONS,
    onActionClick,
    showEmpty = true
  } = props

  const {hasAccess} = useAuth()
  const {t} = useTranslation(["common"])

  const visibleActions: Set<GeofenceEventAction> = useMemo(() => {
    const enabledActions = new Set<GeofenceEventAction>()
    const actionsSet = actions ? new Set<GeofenceEventAction>(actions) : new Set<GeofenceEventAction>()

    if (hasAccess("geofenceevent_edit")) {
      if (actionsSet.has("EDIT")) {
        enabledActions.add("EDIT")
      }
    }

    if (hasAccess("geofenceevent_delete")) {
      if (actionsSet.has("DELETE")) {
        enabledActions.add("DELETE")
      }
    }

    return enabledActions
  }, [geofenceEvent, actions, hasAccess])

  const {
    onGeofenceEventActionClick
  } = useGeofenceEventAction()

  const _onActionClick = useCallback((action: GeofenceEventAction) => {
    if (onActionClick) {
      onActionClick(action)
    } else {
      onGeofenceEventActionClick(geofenceEvent, action)
    }
  }, [onActionClick, onGeofenceEventActionClick, geofenceEvent])

  return (
    <>
      {(showEmpty && visibleActions.size === 0) &&
        <Menu.Item>
          {t("common.action.none", "No Actions")}
        </Menu.Item>
      }
      {visibleActions.has("EDIT") &&
        <Menu.Item icon={<IconEdit size={14}/>} onClick={() => _onActionClick("EDIT")}>
          {t("common.action.edit", "Edit")}
        </Menu.Item>
      }
      {visibleActions.has("DELETE") &&
        <>
          {visibleActions.size > 1 && <Menu.Divider/>}
          <Menu.Item
            onClick={() => _onActionClick("DELETE")}
            icon={<IconTrash size={14}/>}
            color="red">
            {t("common.action.delete", "Delete")}
          </Menu.Item>
        </>
      }
    </>
  )
}
